/**
 * Camada de áudio da chamada (Fase 2 VoIP) — captura do microfone e playback,
 * ambos em 16kHz mono (formato nativo do protocolo de voz do WhatsApp).
 *
 * AudioContext a 16kHz evita resample manual: o browser faz a conversão do
 * hardware (48kHz) por nós nas duas direções. Echo/noise cancellation vêm do
 * getUserMedia. Worklets inline via Blob URL — sem arquivo público extra.
 *
 * Fio (WebSocket, binário): Int16 PCM little-endian.
 */

const CAPTURE_WORKLET = `
class CaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buf = new Float32Array(320); // 20ms @ 16kHz
    this.off = 0;
  }
  process(inputs) {
    const ch = inputs[0]?.[0];
    if (!ch) return true;
    let i = 0;
    while (i < ch.length) {
      const take = Math.min(ch.length - i, this.buf.length - this.off);
      this.buf.set(ch.subarray(i, i + take), this.off);
      this.off += take;
      i += take;
      if (this.off === this.buf.length) {
        this.port.postMessage(this.buf.slice());
        this.off = 0;
      }
    }
    return true;
  }
}
registerProcessor('voip-capture', CaptureProcessor);
`;

const PLAYBACK_WORKLET = `
class PlaybackProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.queue = [];
    this.current = null;
    this.off = 0;
    this.port.onmessage = (e) => {
      this.queue.push(e.data);
      // Jitter cap ~600ms: se a fila crescer demais (aba em background), corta
      // o excesso pra latência não acumular.
      while (this.queue.length > 30) this.queue.shift();
    };
  }
  process(_inputs, outputs) {
    const out = outputs[0]?.[0];
    if (!out) return true;
    let i = 0;
    while (i < out.length) {
      if (!this.current || this.off >= this.current.length) {
        this.current = this.queue.shift() ?? null;
        this.off = 0;
        if (!this.current) {
          out.fill(0, i);
          return true;
        }
      }
      const take = Math.min(out.length - i, this.current.length - this.off);
      out.set(this.current.subarray(this.off, this.off + take), i);
      this.off += take;
      i += take;
    }
    return true;
  }
}
registerProcessor('voip-playback', PlaybackProcessor);
`;

function int16ToFloat32(buf: ArrayBuffer): Float32Array {
  const view = new Int16Array(buf);
  const out = new Float32Array(view.length);
  for (let i = 0; i < view.length; i += 1) out[i] = (view[i] ?? 0) / 32768;
  return out;
}

function float32ToInt16(pcm: Float32Array): ArrayBuffer {
  const out = new Int16Array(pcm.length);
  for (let i = 0; i < pcm.length; i += 1) {
    const v = Math.max(-1, Math.min(1, pcm[i] ?? 0));
    out[i] = Math.round(v * 32767);
  }
  return out.buffer;
}

export class VoipAudio {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private playbackNode: AudioWorkletNode | null = null;
  private ringOsc: OscillatorNode | null = null;
  private ringGain: GainNode | null = null;
  private ringTimer: ReturnType<typeof setInterval> | null = null;

  /** Pede o microfone e liga captura + playback. Chamar num gesto do usuário. */
  async start(onMicFrame: (frame: ArrayBuffer) => void): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    this.ctx = new AudioContext({ sampleRate: 16000 });
    const blob = (code: string) =>
      URL.createObjectURL(new Blob([code], { type: 'application/javascript' }));
    await this.ctx.audioWorklet.addModule(blob(CAPTURE_WORKLET));
    await this.ctx.audioWorklet.addModule(blob(PLAYBACK_WORKLET));

    const source = this.ctx.createMediaStreamSource(this.stream);
    const capture = new AudioWorkletNode(this.ctx, 'voip-capture');
    capture.port.onmessage = (e) => onMicFrame(float32ToInt16(e.data as Float32Array));
    source.connect(capture);

    this.playbackNode = new AudioWorkletNode(this.ctx, 'voip-playback');
    this.playbackNode.connect(this.ctx.destination);
  }

  /** Frame binário do WS (voz do cliente) → alto-falante. */
  play(frame: ArrayBuffer): void {
    this.playbackNode?.port.postMessage(int16ToFloat32(frame));
  }

  /** Ringback de telefonia (425Hz, cadência BR 1s on / 4s off) enquanto o
   *  destino toca. Gerado no próprio AudioContext — sem asset. */
  startRingback(): void {
    if (!this.ctx || this.ringOsc) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.frequency.value = 425;
    gain.gain.value = 0;
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start();
    this.ringOsc = osc;
    this.ringGain = gain;
    const pulse = (): void => {
      if (!this.ringGain || !this.ctx) return;
      const t = this.ctx.currentTime;
      this.ringGain.gain.setValueAtTime(0.12, t);
      this.ringGain.gain.setValueAtTime(0, t + 1);
    };
    pulse();
    this.ringTimer = setInterval(pulse, 5000);
  }

  stopRingback(): void {
    if (this.ringTimer) clearInterval(this.ringTimer);
    this.ringTimer = null;
    this.ringOsc?.stop();
    this.ringOsc?.disconnect();
    this.ringGain?.disconnect();
    this.ringOsc = null;
    this.ringGain = null;
  }

  async stop(): Promise<void> {
    this.stopRingback();
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
    this.playbackNode = null;
    await this.ctx?.close().catch(() => undefined);
    this.ctx = null;
  }
}
